import { useEffect, useRef, useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  Badge, Box, Button, Flex, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Select,
  Spinner, Tbody, Td, Text, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaPlus, FaSearch, FaTruckLoading } from "react-icons/fa";
import Layout from "../../../components/Layout";
import { crm } from "../../../theme";
import { FiscalInvoiceListItem, getSaleInvoices, getSaleSources, SaleSource } from "../../../services/fiscalService";
import { brl } from "../../process/components/duimp";
import { TableCard, tdProps, thProps } from "../../process/components/tableStyles";
import { operationLabel, statusInfo } from "../../process/components/fiscalLabels";
import FiscalInvoiceModal from "../../process/components/FiscalInvoiceModal";
import SaleModal from "./SaleModal";

/** Escolher a NF de entrada autorizada (com saldo) de onde sai a venda encomenda. */
const SourcePicker = ({ onPick, onClose }: { onPick: (entryId: string) => void; onClose: () => void }) => {
  const toast = useToast();
  const [list, setList] = useState<SaleSource[] | null>(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    getSaleSources().then(setList).catch(() => { setList([]); toast({ status: "error", title: "Não foi possível carregar as NFs de entrada" }); });
  }, []);
  const q = search.trim().toUpperCase();
  const filtered = (list ?? []).filter((s) => !q || [s.processRef, s.clientName, s.entryNumber?.toString(), s.issuerName]
    .some((v) => (v ?? "").toUpperCase().includes(q)));
  return (
    <Modal isOpen onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">Venda encomenda · escolha a NF de entrada</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="20px">
          <Input size="sm" mb="10px" placeholder="Buscar processo, cliente ou nº da entrada" value={search} onChange={(e) => setSearch(e.target.value)} />
          {!list ? (
            <Flex justify="center" py="24px"><Spinner /></Flex>
          ) : filtered.length === 0 ? (
            <Text fontSize="13px" color={crm.textMuted} py="12px">Nenhuma NF de entrada autorizada com saldo.</Text>
          ) : (
            <TableCard>
              <Thead>
                <Tr>
                  <Th {...thProps}>Entrada</Th>
                  <Th {...thProps}>Processo</Th>
                  <Th {...thProps}>Cliente</Th>
                  <Th {...thProps}>Emitente</Th>
                  <Th {...thProps} isNumeric>Itens com saldo</Th>
                  <Th {...thProps}>Autorizada em</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((s) => (
                  <Tr key={s.entryId} cursor="pointer" _hover={{ bg: crm.subtle }} onClick={() => onPick(s.entryId)}>
                    <Td {...tdProps} fontWeight="600">{s.entryNumber ? `Nº ${s.entryNumber}` : "—"}</Td>
                    <Td {...tdProps}>{s.processRef ?? "—"}</Td>
                    <Td {...tdProps}>{s.clientName ?? "—"}</Td>
                    <Td {...tdProps}>{s.issuerName}</Td>
                    <Td {...tdProps} isNumeric>{s.items.filter((i) => i.remaining > 0).length} / {s.items.length}</Td>
                    <Td {...tdProps} color={crm.textMuted}>{s.authorizedAt ? DateTime.fromISO(s.authorizedAt).toFormat("dd/LL/yyyy") : "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </TableCard>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/** Comercial › Vendas: NFs de venda da Distribuidora (própria e encomenda). */
const Sales = () => {
  const toast = useToast();
  const auth = useAuthUser<any>();
  // Mesmo grupo que o servidor deixa emitir (FiscalHandler.CanManage): admin, Administração, Operacional, Financeiro
  const canManage = !!auth?.admin || [2, 3, 5].includes(auth?.position);
  const [list, setList] = useState<FiscalInvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [sale, setSale] = useState<{ entryId?: string; invoiceId?: string } | null>(null);

  const loadSeq = useRef(0);
  /** Só a resposta da última busca vale (filtro trocado no meio do caminho). */
  const load = async () => {
    const n = ++loadSeq.current;
    setLoading(true);
    try {
      const data = await getSaleInvoices({ status: status || undefined, search: search.trim() || undefined });
      if (n === loadSeq.current) setList(data);
    } catch {
      if (n === loadSeq.current) toast({ status: "error", title: "Não foi possível carregar as vendas" });
    } finally {
      if (n === loadSeq.current) setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [status, search]);

  return (
    <Box minH="100vh" bg={crm.pageBg} color={crm.text}>
      <Layout>
        <Box minH="100%" w="100%" bg={crm.pageBg} overflowY="auto" p={{ base: "18px", md: "24px" }}>
          <Flex align="center" gap="12px" mb="22px">
            <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
            <Heading fontSize="28px" color={crm.text}>Vendas</Heading>
          </Flex>
          <Flex justify="space-between" align="center" gap="10px" wrap="wrap" mb="16px">
            <Flex gap="9px" align="center" wrap="wrap">
              <Box position="relative">
                <FaSearch style={{ position: "absolute", left: 10, top: 9, color: crm.textMuted, zIndex: 1 }} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nº, chave, cliente ou processo" size="sm" w="260px" pl="31px" bg={crm.surface} borderColor={crm.borderStrong} />
              </Box>
              <Select size="sm" w="170px" bg={crm.surface} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todas as situações</option>
                {Object.entries(statusInfo).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Select>
            </Flex>
            {canManage && (
              <Flex gap="8px">
                <Button size="sm" variant="outline" leftIcon={<FaTruckLoading />} onClick={() => setPicking(true)}>Venda encomenda</Button>
                <Button size="sm" leftIcon={<FaPlus />} bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={() => setSale({})}>Nova venda</Button>
              </Flex>
            )}
          </Flex>

          {loading ? (
            <Flex justify="center" py="60px"><Spinner thickness="3px" size="xl" /></Flex>
          ) : list.length === 0 ? (
            <Text fontSize="14px" color={crm.textMuted} py="24px">Nenhuma venda.</Text>
          ) : (
            <TableCard>
              <Thead>
                <Tr>
                  <Th {...thProps}>Nota</Th>
                  <Th {...thProps}>Tipo</Th>
                  <Th {...thProps}>Cliente</Th>
                  <Th {...thProps}>Emitente</Th>
                  <Th {...thProps}>Processo</Th>
                  <Th {...thProps}>Situação</Th>
                  <Th {...thProps} isNumeric>Total</Th>
                  <Th {...thProps}>Criada em</Th>
                </Tr>
              </Thead>
              <Tbody>
                {list.map((nf) => (
                  <Tr key={nf.id} cursor="pointer" _hover={{ bg: crm.subtle }} onClick={() => setOpenId(nf.id)}>
                    <Td {...tdProps} fontWeight="600">{nf.number ? `Nº ${nf.number}` : "—"}</Td>
                    <Td {...tdProps}>{operationLabel[nf.operation] ?? nf.operation}</Td>
                    <Td {...tdProps}>{nf.recipientName ?? "—"}</Td>
                    <Td {...tdProps}>
                      <Box>{nf.issuerName}</Box>
                      {(nf.environment ?? nf.issuerEnvironment) === "homologacao" && <Badge colorScheme="purple" fontSize="10px">Homologação</Badge>}
                    </Td>
                    <Td {...tdProps}>{nf.processRef ?? "—"}</Td>
                    <Td {...tdProps}><Badge colorScheme={statusInfo[nf.status]?.color}>{statusInfo[nf.status]?.label ?? nf.status}</Badge></Td>
                    <Td {...tdProps} isNumeric>{brl(nf.total)}</Td>
                    <Td {...tdProps} color={crm.textMuted}>{DateTime.fromISO(nf.createdDate).toFormat("dd/LL/yyyy HH:mm")}</Td>
                  </Tr>
                ))}
              </Tbody>
            </TableCard>
          )}
        </Box>
      </Layout>

      {picking && <SourcePicker onClose={() => setPicking(false)} onPick={(entryId) => { setPicking(false); setSale({ entryId }); }} />}
      {sale && (
        <SaleModal
          entryId={sale.entryId}
          invoiceId={sale.invoiceId}
          onClose={() => setSale(null)}
          onSaved={(id) => { setSale(null); load(); setOpenId(id); }}
        />
      )}
      {openId && (
        <FiscalInvoiceModal
          invoiceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          onEditSale={canManage ? (id) => { setOpenId(null); setSale({ invoiceId: id }); } : undefined}
        />
      )}
    </Box>
  );
};

export default Sales;
